import { useState, useEffect, useRef } from 'react';
import { Box3, BufferGeometry, Matrix4 } from 'three';
import { STLLoader } from 'three/addons/loaders/STLLoader.js';
import { MeshBVH } from 'three-mesh-bvh';
import type { ColoredBlob } from '@/contexts/BlobContext';
import type { ConstraintDef } from '@/lib/scad-preprocessor';

export type ConstraintViolation = {
  type: ConstraintDef['type'];
  message?: string;
  ghostIndex: number;
};

export type ConstraintCheckResult = {
  violations: ConstraintViolation[];
  isChecking: boolean;
  error: string | null;
};

/**
 * Check each `// @constraint` annotation against the current compiled geometry.
 *
 * `build-volume`:
 *   The ghost is the allowed envelope. Violated when any non-ghost geometry
 *   extends outside it (bbox fast-path) or pierces its surface (BVH check).
 *
 * `keep-out`:
 *   The ghost is a forbidden zone. Violated when any non-ghost geometry
 *   intersects the ghost surface OR sits entirely inside it.
 *
 * `clearance`:
 *   The ghost is a reference surface. Violated when any non-ghost geometry
 *   comes within `constraint.distance` mm of it (or intersects it outright).
 *
 * All geometries share OpenSCAD world space, so an identity Matrix4 is used
 * for all BVH transform arguments.
 */
export function useConstraintCheck(
  coloredBlobs: ColoredBlob[],
  constraints: ConstraintDef[],
): ConstraintCheckResult {
  const [violations, setViolations] = useState<ConstraintViolation[]>([]);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Generation counter — incremented on every new check to cancel stale async work.
  const generationRef = useRef(0);

  useEffect(() => {
    // Nothing to check if there are no constraint annotations.
    if (constraints.length === 0) {
      setViolations([]);
      setError(null);
      setIsChecking(false);
      return;
    }

    const ghostBlobs = coloredBlobs.filter((cb) => cb.isGhost);
    const nonGhostBlobs = coloredBlobs.filter(
      (cb) => !cb.isGhost && !cb.excludedFromExport,
    );

    // Without both ghost and non-ghost geometry there is nothing to compare.
    if (ghostBlobs.length === 0 || nonGhostBlobs.length === 0) {
      setViolations([]);
      setError(null);
      setIsChecking(false);
      return;
    }

    const thisGeneration = ++generationRef.current;
    setIsChecking(true);
    setError(null);

    const IDENTITY = new Matrix4();

    async function runCheck() {
      const ghostGeometries: BufferGeometry[] = [];
      const nonGhostGeometries: BufferGeometry[] = [];

      try {
        const loader = new STLLoader();

        // Parse ghost blobs.
        for (const cb of ghostBlobs) {
          const buffer = await cb.blob.arrayBuffer();
          if (generationRef.current !== thisGeneration) return;
          ghostGeometries.push(loader.parse(buffer));
        }

        // Parse non-ghost blobs.
        for (const cb of nonGhostBlobs) {
          const buffer = await cb.blob.arrayBuffer();
          if (generationRef.current !== thisGeneration) return;
          nonGhostGeometries.push(loader.parse(buffer));
        }

        if (generationRef.current !== thisGeneration) return;

        // Pre-compute bounding boxes for all geometries (used by multiple checks).
        const ghostBox = new Box3();
        for (const geom of ghostGeometries) {
          geom.computeBoundingBox();
          if (geom.boundingBox) ghostBox.union(geom.boundingBox);
        }

        const nonGhostCombinedBox = new Box3();
        for (const geom of nonGhostGeometries) {
          geom.computeBoundingBox();
          if (geom.boundingBox) nonGhostCombinedBox.union(geom.boundingBox);
        }

        const found: ConstraintViolation[] = [];

        for (const constraint of constraints) {
          const ghostGeom = ghostGeometries[constraint.ghostIndex];
          if (!ghostGeom) continue; // ghost index out of range — skip

          ghostGeom.computeBoundingBox();
          const thisGhostBox = ghostGeom.boundingBox!;

          // ----------------------------------------------------------------
          // build-volume: model must fit entirely inside the ghost envelope.
          // ----------------------------------------------------------------
          if (constraint.type === 'build-volume') {
            // Fast path: if non-ghost bbox is fully inside ghost bbox,
            // no surface piercing is possible.
            if (thisGhostBox.containsBox(nonGhostCombinedBox)) continue;

            // BVH surface intersection check: model sticks through the shell.
            const ghostBvh = new MeshBVH(ghostGeom);
            let surfaceIntersects = false;
            for (const nonGhostGeom of nonGhostGeometries) {
              if (ghostBvh.intersectsGeometry(nonGhostGeom, IDENTITY)) {
                surfaceIntersects = true;
                break;
              }
            }

            // Violated when the model pierces the surface OR sits entirely
            // outside the ghost bbox (no intersection but still not inside).
            if (
              surfaceIntersects ||
              !thisGhostBox.containsBox(nonGhostCombinedBox)
            ) {
              found.push({
                type: constraint.type,
                message: constraint.message,
                ghostIndex: constraint.ghostIndex,
              });
            }
            continue;
          }

          // ----------------------------------------------------------------
          // keep-out: no model geometry may enter or intersect the ghost.
          // ----------------------------------------------------------------
          if (constraint.type === 'keep-out') {
            // Fast path: bboxes don't even overlap — nothing can be inside.
            if (!thisGhostBox.intersectsBox(nonGhostCombinedBox)) continue;

            const ghostBvh = new MeshBVH(ghostGeom);

            // Check 1: any non-ghost surface pierces the ghost surface.
            let violated = false;
            for (const nonGhostGeom of nonGhostGeometries) {
              if (ghostBvh.intersectsGeometry(nonGhostGeom, IDENTITY)) {
                violated = true;
                break;
              }
            }

            // Check 2: non-ghost bbox is entirely inside the ghost bbox
            // (model is fully enveloped — no surface crossing, but still inside).
            if (!violated && thisGhostBox.containsBox(nonGhostCombinedBox)) {
              violated = true;
            }

            if (violated) {
              found.push({
                type: constraint.type,
                message: constraint.message,
                ghostIndex: constraint.ghostIndex,
              });
            }
            continue;
          }

          // ----------------------------------------------------------------
          // clearance: model must stay >= distance mm away from the ghost.
          // ----------------------------------------------------------------
          if (constraint.type === 'clearance') {
            const requiredDist = constraint.distance ?? 0;

            // Fast path: if the bboxes are more than requiredDist apart on
            // every axis, no triangle can be within range.
            const ghostBoxExpanded = thisGhostBox
              .clone()
              .expandByScalar(requiredDist);
            if (!ghostBoxExpanded.intersectsBox(nonGhostCombinedBox)) continue;

            const ghostBvh = new MeshBVH(ghostGeom);

            let violated = false;
            for (const nonGhostGeom of nonGhostGeometries) {
              // First check for surface intersection (distance = 0).
              if (ghostBvh.intersectsGeometry(nonGhostGeom, IDENTITY)) {
                violated = true;
                break;
              }

              // Then check minimum surface-to-surface distance.
              // closestPointToGeometry returns null when nothing is within
              // maxThreshold, so a non-null result means something is closer
              // than requiredDist.
              if (requiredDist > 0) {
                const hit = ghostBvh.closestPointToGeometry(
                  nonGhostGeom,
                  IDENTITY,
                  undefined,
                  undefined,
                  0, // minThreshold: include all distances
                  requiredDist, // maxThreshold: bail early if farther than needed
                );
                if (hit !== null) {
                  violated = true;
                  break;
                }
              }
            }

            if (violated) {
              found.push({
                type: constraint.type,
                message: constraint.message,
                ghostIndex: constraint.ghostIndex,
              });
            }
            continue;
          }
        }

        if (generationRef.current !== thisGeneration) return;

        setViolations(found);
      } catch (err) {
        if (generationRef.current !== thisGeneration) return;
        setError(
          err instanceof Error ? err.message : 'Constraint check failed.',
        );
      } finally {
        for (const geom of [...ghostGeometries, ...nonGhostGeometries]) {
          geom.dispose();
        }
        if (generationRef.current === thisGeneration) {
          setIsChecking(false);
        }
      }
    }

    runCheck();
  }, [coloredBlobs, constraints]);

  return { violations, isChecking, error };
}
